import React, { useEffect, useState, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Plus, Edit, Trash2, Copy } from 'lucide-react';
import { api } from '../../api/client';
import { toast } from 'sonner';
import MediaUpload from '../../components/MediaUpload';
import SeoYoast from '../../components/SeoYoast';

const empty = { name: '', slug: '', price: 0, old_price: null, image: '', category: '15k', flavor: '', stock: 0, sku: '', puffs: 15000, description: '', featured: true, published: true, seo_title: '', seo_description: '', seo_keyphrase: '' };

const AdminProducts = () => {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = useCallback(() => api.listProducts().then(setItems).catch((err) => console.error('listProducts failed:', err)), []);
  useEffect(() => {
    load();
    api.listCategories().then(setCats).catch((err) => console.error('listCategories failed:', err));
  }, [load]);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => { setEditing(p.id); setForm({ ...empty, ...p, old_price: p.old_price || null }); setOpen(true); };
  const save = async () => {
    if (!form.name) return toast.error('Name is required');
    try {
      if (editing) {
        await api.updateProduct(editing, form);
        toast.success('Product updated');
      } else {
        await api.createProduct(form);
        toast.success('Product added');
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try { await api.deleteProduct(id); toast.success('Deleted'); load(); } catch { toast.error('Delete failed'); }
  };
  const clone = async (p) => {
    const { id, _id, created_at, updated_at, ...rest } = p; // strip server fields
    const baseSlug = (p.slug || p.name || 'product').replace(/-copy(-\d+)?$/, '');
    const copy = {
      ...rest,
      name: `${p.name} (Copy)`,
      slug: `${baseSlug}-copy-${Date.now().toString().slice(-5)}`,
      sku: p.sku ? `${p.sku}-COPY` : '',
      published: false,
      featured: false,
    };
    try { await api.createProduct(copy); toast.success(`Cloned "${p.name}"`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Clone failed'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-zinc-400">Manage your full catalog — categories, pricing, stock, SEO and visibility per product.</p>
        <Button onClick={openNew} className="btn-gold" data-testid="product-new-btn"><Plus className="h-4 w-4 mr-2" /> New product</Button>
      </div>

      <Card className="bg-zinc-950 border-zinc-900 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/60">
            <tr className="text-left text-zinc-400">
              <th className="p-3">Product</th><th className="p-3">Category</th><th className="p-3">Price</th><th className="p-3">Stock</th><th className="p-3">SKU</th><th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-zinc-500">No products yet</td></tr>}
            {items.map((p) => (
              <tr key={p.id} className="border-t border-zinc-900 hover:bg-zinc-900/30">
                <td className="p-3"><div className="flex items-center gap-3"><img src={p.image} className="w-10 h-10 rounded object-cover" alt="" /><div><p className="text-zinc-100">{p.name}</p><p className="text-zinc-500 text-xs">{p.flavor}</p></div></div></td>
                <td className="p-3"><Badge variant="outline" className="border-zinc-800 text-zinc-300">{(cats.find((c) => c.slug === p.category)?.name) || p.category}</Badge></td>
                <td className="p-3 text-[#d4af37]">€{Number(p.price).toFixed(2)}</td>
                <td className="p-3 text-zinc-300">{p.stock}</td>
                <td className="p-3 text-zinc-500">{p.sku}</td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="ghost" className="text-zinc-300 hover:text-[#d4af37]" onClick={() => openEdit(p)} data-testid={`product-edit-${p.id}`}><Edit className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" className="text-zinc-300 hover:text-emerald-400" onClick={() => clone(p)} title="Duplicate product" data-testid={`product-clone-${p.id}`}><Copy className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" className="text-zinc-300 hover:text-rose-500" onClick={() => del(p.id)} data-testid={`product-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-900 text-zinc-100 max-w-5xl max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle className="serif-italic text-[#d4af37] text-2xl">{editing ? 'Edit product' : 'New product'}</DialogTitle></DialogHeader>
          <div className="grid lg:grid-cols-[1fr,360px] gap-6 mt-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Image</Label>
                <div className="mt-1">
                  <MediaUpload value={form.image} onChange={(url) => setForm({ ...form, image: url })} testIdPrefix="product-image" />
                </div>
              </div>
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value, slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') })} className="bg-transparent border-zinc-800 mt-1" data-testid="product-name" /></div>
              <div><Label>Slug</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="bg-transparent border-zinc-800 mt-1" data-testid="product-slug" /></div>
              <div><Label>Category</Label><Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}><SelectTrigger className="bg-transparent border-zinc-800 mt-1"><SelectValue /></SelectTrigger><SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.slug}>{c.name}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Flavor</Label><Input value={form.flavor} onChange={(e) => setForm({ ...form, flavor: e.target.value })} className="bg-transparent border-zinc-800 mt-1" /></div>
              <div><Label>Price (€)</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })} className="bg-transparent border-zinc-800 mt-1" /></div>
              <div><Label>Compare-at price (€)</Label><Input type="number" value={form.old_price || ''} onChange={(e) => setForm({ ...form, old_price: e.target.value ? parseFloat(e.target.value) : null })} className="bg-transparent border-zinc-800 mt-1" /></div>
              <div><Label>SKU</Label><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} className="bg-transparent border-zinc-800 mt-1" /></div>
              <div><Label>Stock</Label><Input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: parseInt(e.target.value) || 0 })} className="bg-transparent border-zinc-800 mt-1" /></div>
              <div><Label>Puffs</Label><Input type="number" value={form.puffs} onChange={(e) => setForm({ ...form, puffs: parseInt(e.target.value) || 0 })} className="bg-transparent border-zinc-800 mt-1" /></div>
              <div className="md:col-span-2"><Label>Description</Label><Textarea rows={6} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-transparent border-zinc-800 mt-1" data-testid="product-description" /></div>

              {/* SEO fields */}
              <div className="md:col-span-2 border-t border-zinc-900 pt-4 mt-2">
                <p className="text-[#d4af37] tracking-[0.2em] text-[10px] uppercase mb-2">Search engine listing</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div><Label>SEO title</Label><Input value={form.seo_title || ''} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} className="bg-transparent border-zinc-800 mt-1" data-testid="product-seo-title" /></div>
                  <div><Label>Meta description</Label><Input value={form.seo_description || ''} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} className="bg-transparent border-zinc-800 mt-1" data-testid="product-seo-desc" /></div>
                </div>
              </div>

              <div className="flex items-center justify-between md:col-span-2 border border-zinc-900 rounded p-3"><span className="text-zinc-300 text-sm">Featured on home page</span><Switch checked={!!form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} /></div>
              <div className="flex items-center justify-between md:col-span-2 border border-zinc-900 rounded p-3"><span className="text-zinc-300 text-sm">Published (visible on storefront)</span><Switch checked={!!form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} /></div>
            </div>

            {/* Right column: live Yoast-style SEO scoring */}
            <div className="lg:sticky lg:top-0 self-start">
              <SeoYoast
                product={form}
                focusKeyword={form.seo_keyphrase || ''}
                onFocusKeywordChange={(v) => setForm({ ...form, seo_keyphrase: v })}
              />
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <Button variant="outline" className="btn-outline-gold" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="btn-gold" onClick={save} data-testid="product-save">{editing ? 'Update' : 'Create'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminProducts;
